/**
 * RatingRegisterContainer
 * --
 */
import React from 'react';
import RatingRegisterPresenter from './RatingRegisterPresenter';
import { useCreateGrade, useUpdateGrade } from 'hooks/GradeHooks';
import MessageAlert from 'utils';

const INITIAL_DATA = {
  grade_name: '',
  grade_reason: '',
  grade_description: {
    기준: '',
    설명: '',
  },
  grade_condition_amount: 0,
  transaction_count: 0,
  is_enable: 'Y',
  is_fixed: 'N',
  coupon: [],
};

const RatingRegisterContainer = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const { type, data, coupons, onChangeGrade, onSuccess, setIsLoading } = props;

  /* ===== Mutate ===== */
  const { createGrade } = useCreateGrade();
  const { updateGrade } = useUpdateGrade();

  const mutate = {
    CREATE: createGrade,
    UPDATE: updateGrade,
  };

  /* ===== STATE ===== */
  // 쿠폰 모달
  const [isOpened, setIsOpened] = React.useState(false);
  // 등급 입력값
  const [inputValue, setInputValue] = React.useState(
    data ? data : INITIAL_DATA
  );
  const [selectedList, setSelectedList] = React.useState([]);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 데이터 바인딩
   * --
   * @param {string} t
   * @param {string} v
   */
  const handleChange = (t, v) => {
    setInputValue((prev) => ({
      ...prev,
      [t]: v,
    }));
  };
  /**
   * 쿠폰 선택 핸들러
   * --
   * @param {string} t
   * @param {object | array} obj
   */
  const handleSelect = (t, obj) => {
    const selectType = t.toLocaleUpperCase();

    switch (selectType) {
      case 'CHECKED': // 개별 선택
        const couponValue = {
          key: obj.coupon_id,
          coupon_id: obj.coupon_id,
          coupon_name: obj.coupon_name,
          num: obj.num ?? 1,
        };
        setSelectedList((prev) => [...prev, couponValue]);
        return;
      case 'UNCHECKED': // 개별 선택 해제
        setSelectedList((prev) =>
          prev.filter((select) => select.key !== obj.key)
        );
        return;
      case 'ALL-CHECKED': // 노출된 목록 전체 선택
        const allCheckedValue = obj?.map((select) => {
          const matedCoupon = selectedList.find(
            (prev) => prev.key === select.key
          );
          if (matedCoupon) {
            return matedCoupon;
          }
          return {
            key: select.coupon_id,
            coupon_id: select.coupon_id,
            coupon_name: select.coupon_name,
            num: 1,
          };
        });
        setSelectedList(allCheckedValue);
        return;
      case 'ALL-UNCHECKED': // 노출된 목록 전체 선택 해제
        setSelectedList([]);
        return;
      case 'CONFIRM': // 체크한 쿠폰 목록 저장
        const confirmValue = selectedList?.map((select) => {
          const matedCoupon = inputValue?.couponList?.find(
            (prev) => prev.key === select.key
          );
          if (matedCoupon) {
            return matedCoupon;
          }

          return {
            key: select.coupon_id,
            coupon_id: select.coupon_id,
            coupon_name: select.coupon_name,
            num: 1,
          };
        });
        handleChange('couponList', confirmValue);
        return;
      case 'CANCEL': //취소 및 화면 밖 터치
        const cancelValue = inputValue?.couponList?.map((coupon) => ({
          key: coupon.coupon_id,
          coupon_id: coupon.coupon_id,
          coupon_name: coupon.coupon_name,
          num: coupon.num,
        }));
        setSelectedList(cancelValue);
        return;
      default:
        return;
    }
  };

  /**
   * 등급 데이터 수정/생성
   * --
   * @param {string} t
   */
  const handleSubmit = async (t) => {
    let success = false;
    const msg = t === 'CREATE' ? '생성' : '수정';
    if (!window.confirm(`${msg} 하시겠습니까?`)) return;
    try {
      const newGradeData = {
        ...inputValue,
        coupon:
          inputValue.couponList && inputValue.couponList.length
            ? inputValue.couponList.reduce((acc, cur) => {
                const arr = Array.from({ length: cur.num }, () => ({
                  coupon_id: cur.coupon_id,
                }));
                return acc.concat(arr);
              }, [])
            : null,
      };
      const { status, data } = await mutate[t](newGradeData);
      if (status !== 200) {
        MessageAlert.info(`등급 ${msg}에 실패하였습니다.`);
        return;
      }
      onChangeGrade(
        t,
        t === 'CREATE' ? { ...data, ...newGradeData, user: [] } : newGradeData
      );
      MessageAlert.success(`등급이 성공적으로 ${msg}되었습니다.`);
      success = true;
    } catch (err) {
    } finally {
      setTimeout(() => {
        setIsLoading(false);
        if (success) {
          onSuccess();
        }
      }, 300);
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 쿠폰 테이블 데이터 정제
   * --
   */
  const newCouponList = React.useMemo(() => {
    if (!coupons.length) return [];

    return coupons.map((item) => ({
      key: item?.coupon_id,
      ...item,
    }));
  }, [coupons]);

  /**
   * 등급 수정 시 선택된 쿠폰 초기값 설정
   * --
   */
  React.useEffect(() => {
    if (!data) return;

    const { coupon } = data;

    // 등록된 쿠폰이 없을 경우 처리
    if (!coupon) return;

    // 같은 쿠폰 id를 가진 값끼리 매핑
    const mappingCoupon = Object.values(
      coupon.reduce((acc, cur) => {
        const { coupon_id } = cur;
        if (!acc[coupon_id]) {
          acc[coupon_id] = {
            coupon_id,
            key: coupon_id,
            coupon_name: coupons.find((c) => c.coupon_id === coupon_id)
              ?.coupon_name,
            num: 0,
          };
        }
        acc[coupon_id].num++;
        return acc;
      }, {})
    );
    setSelectedList(mappingCoupon);
    // handleChange('coupon', mappingCoupon);
  }, [newCouponList, data, coupons]);

  React.useEffect(() => {
    if (data) {
      setInputValue(data);
    } else {
      setInputValue(INITIAL_DATA);
      setSelectedList([]);
    }
  }, [data]);

  /* ===== RENDER ===== */
  return (
    <>
      <RatingRegisterPresenter
        type={type}
        inputValue={inputValue}
        coupons={newCouponList}
        onChange={handleChange}
        onSelect={handleSelect}
        selectedList={selectedList}
        onSelectedList={setSelectedList}
        onSubmit={handleSubmit}
        onSuccess={onSuccess}
        isOpened={isOpened}
        setIsOpened={setIsOpened}
      />
    </>
  );
};

export default RatingRegisterContainer;
